if (!customElements.get('slideout-mega-menu-component')) {
    class SlideoutMegaMenu extends HTMLElement {
      constructor() { 
        super(); 
        this._onTabClick = (event) => {
          this.switchTab(event.currentTarget);
        };
        this.isOpen = false;
      }

      get buttons() {
        return this.querySelectorAll('.slideout-tab-button');
      }

      open() {
        const parentSection = this.closest('.section-slidemega-menu');
        if (parentSection) {
          // The closed panel is display:none (sections/slideout-mega-menu.liquid, so its
          // thumbnails aren't downloaded): render it first, then slide it in on the next style
          // calculation so the transition still runs.
          clearTimeout(this._unrenderTimer);
          parentSection.classList.add('is-rendered');
          void parentSection.offsetWidth;
          parentSection.classList.add('is-open');
        }
        document.body.style.overflow = 'hidden';
        this.isOpen = true;
        const closeBtn = this.querySelector('.slideout-close-button');
        if (closeBtn) closeBtn.focus();
      }

      /**
       * @param {{ leavingPage?: boolean }} [options] - `leavingPage` (assets/navigation-feedback.js): a link
       *   in the menu is opening another page. The panel slides out but stays rendered, because taking its
       *   links out of layout cancels the browser's prefetch of that page, and focus stays where it is.
       */
      close({ leavingPage = false } = {}) {
        const parentSection = this.closest('.section-slidemega-menu');
        if (parentSection) {
          parentSection.classList.remove('is-open');
          clearTimeout(this._unrenderTimer);
          // Take the panel out of layout again once the 0.3s slide-out has finished.
          if (!leavingPage) {
            this._unrenderTimer = setTimeout(() => {
              if (!this.isOpen) parentSection.classList.remove('is-rendered');
            }, 350);
          }
        }
        document.body.style.overflow = '';
        this.isOpen = false;
        if (leavingPage) return;
        const trigger = document.querySelector('[data-shop-products-trigger]');
        if (trigger) trigger.focus();
      }

      connectedCallback() { 
        setTimeout(() => {
          this.init();
        }, 0);

        // Handle submenu toggles, close button clicks
        this.addEventListener('click', (event) => {
          const closeButton = event.target.closest('.slideout-close-button');
          if (closeButton) {
            this.close();
            return;
          }

          const button = event.target.closest('[data-submenu-id]');
          if (button) {
            const submenuId = button.getAttribute('data-submenu-id');
            const submenu = this.querySelector(`#${submenuId}`);
            if (submenu) {
              const contentsContainer = this.querySelector('.slideout-mega-menu__contents');
              if (contentsContainer) {
                const parentPanel = button.closest('.slideout-submenu-panel');
                if (parentPanel) {
                  // Level 3 panel: position relative to Level 2 panel's scroll position
                  submenu.style.top = `${parentPanel.scrollTop}px`;
                } else {
                  // Level 2 panel: position relative to main contents container scroll position
                  submenu.style.top = `${contentsContainer.scrollTop}px`;
                }
              }
              this.classList.add('submenu-open');
              submenu.classList.add('active');
            }
          }

          const backButton = event.target.closest('.slideout-submenu-back');
          if (backButton) {
            const submenu = backButton.closest('.slideout-submenu-panel');
            if (submenu) {
              submenu.classList.remove('active');
              // Only remove submenu-open class if there are no more active submenu panels
              const activePanels = this.querySelectorAll('.slideout-submenu-panel.active');
              if (activePanels.length === 0) {
                this.classList.remove('submenu-open');
              }
            }
          }
        });

        // Close on Escape press
        document.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && this.isOpen) {
            this.close();
          }
        });

        // Close on clicking outside
        document.addEventListener('click', (event) => {
          if (this.isOpen && !this.contains(event.target) && !event.target.closest('[data-shop-products-trigger]')) {
            this.close();
          }
        });

        // Toggle on trigger click
        document.addEventListener('click', (event) => {
          const trigger = event.target.closest('[data-shop-products-trigger]');
          if (trigger) {
            if (trigger.tagName === 'SUMMARY') {
              event.preventDefault();
            }
            if (this.isOpen) {
              this.close();
            } else {
              this.open();
            }
          }
        });

        // Support Shopify Theme Editor select/deselect section event
        const parentSection = this.closest('.shopify-section');
        if (parentSection) {
          document.addEventListener('shopify:section:deselect', (event) => {
            if (event.target === parentSection) {
              this.close();
            }
          });
        }

        // Support Shopify Theme Editor select event
        this.addEventListener('shopify:block:select', (event) => {
          const blockId = event.detail.blockId;
          const matchingButton = Array.from(this.buttons).find(btn => {
            const targetId = btn.getAttribute('data-target-id') || '';
            const blockName = targetId.replace('slideout-block-', '');
            return blockId.includes(blockName);
          });
          if (matchingButton) {
            this.switchTab(matchingButton);
          }
          this.syncTabTitles();
        });
      }

      init() {
        const parentSection = this.closest('.section-slidemega-menu');
        if (parentSection) {
          parentSection.classList.add('slideout-initialized');
        }

        this.buttons.forEach(button => {
          button.addEventListener('click', this._onTabClick);
        });

        // Initialize state
        const firstButton = this.buttons[0]; 
        if (firstButton) {
          this.switchTab(firstButton); 
        }

        // Sync custom tab titles from blocks
        this.syncTabTitles();
      }

      syncTabTitles() {
        const contents = this.querySelectorAll('.slideout-block-wrapper');
        contents.forEach((content, idx) => {
          const tabTitle = content.getAttribute('data-tab-title');
          const matchingButton = this.buttons[idx];
          if (matchingButton && tabTitle) {
            matchingButton.textContent = tabTitle;
          }
        });
      }

      switchTab(selectedButton) {
        const selectedIndex = parseInt(selectedButton.getAttribute('data-index'), 10);
        this.syncTabTitles();

        // Reset all submenu panels to closed when tab changes
        this.querySelectorAll('.slideout-submenu-panel.active').forEach(panel => {
          panel.classList.remove('active');
          panel.style.top = '';
        });
        this.classList.remove('submenu-open');

        // Update button states
        this.buttons.forEach((btn, idx) => {
          const isActive = idx === selectedIndex;
          btn.classList.toggle('active', isActive);
          btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        // Query content wrappers currently rendered inside content container
        const contents = this.querySelectorAll('.slideout-block-wrapper');
        contents.forEach((content, idx) => {
          const isActive = idx === selectedIndex;
          content.classList.toggle('active', isActive);

          const parentShopifyBlock = content.closest('[id^="shopify-block-"]');
          if (parentShopifyBlock) {
            parentShopifyBlock.classList.toggle('active', isActive);
          }
        });
      }
    }

    customElements.define('slideout-mega-menu-component', SlideoutMegaMenu);
  }
